import { useState } from 'react';
import { useCreateInvite } from '../../hooks/useUsers';
import { useAuth } from '../../hooks/useAuth';
import Spinner from '../common/Spinner';

interface Props {
  onSuccess?: () => void;
}

export default function InviteForm({ onSuccess }: Props) {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'billing' | 'tech'>('tech');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { mutate: createInvite, isPending, error } = useCreateInvite();

  const canInviteAdmin = user?.role === 'admin';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);

    createInvite(
      { email, role },
      {
        onSuccess: (data) => {
          setEmail('');
          setRole('tech');
          setSuccessMessage(`Invite sent to ${data.email}`);
          onSuccess?.();
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@example.com"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="sm:w-40">
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
            Role
          </label>
          <select
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'billing' | 'tech')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="tech">Tech</option>
            <option value="billing">Billing</option>
            {canInviteAdmin && <option value="admin">Admin</option>}
          </select>
        </div>

        <div className="sm:self-end">
          <button
            type="submit"
            disabled={isPending || !email}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isPending ? (
              <>
                <Spinner size="sm" className="border-white border-t-transparent" />
                Sending...
              </>
            ) : (
              'Send Invite'
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600">
          {error instanceof Error ? error.message : 'Failed to send invite'}
        </div>
      )}

      {successMessage && (
        <div className="text-sm text-green-600">
          {successMessage}
        </div>
      )}
    </form>
  );
}
